import { ISelectedInformation } from './selected-information.interface';
import { ISelectedKnowledgeDocument } from './selected-knowledge-document.interface';

export type MessageReferenceType = 'knowledge_document' | 'addressable_information';

export interface IKnowledgeDocumentReference {
  type: 'knowledge_document';
  id: string;
  title: string;
  originalFilename?: string;
  detectedMime?: string;
}

export interface IInformationReference {
  type: 'addressable_information';
  id: string;
  title: string;
  generatedAt?: string;
}

export type IMessageReference = IKnowledgeDocumentReference | IInformationReference;

export interface IKnowledgeDocumentReferenceRaw {
  type: 'knowledge_document';
  id: string;
  title: string;
  mimetype?: string | null;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface IInformationReferenceRaw {
  type: 'addressable_information';
  id: string;
  title: string;
  generated_at?: string | null;
}
/* eslint-enable @typescript-eslint/naming-convention */

export type IMessageReferenceRaw = IKnowledgeDocumentReferenceRaw | IInformationReferenceRaw;

export const toMessageReference = (raw: IMessageReferenceRaw): IMessageReference =>
  raw.type === 'addressable_information'
    ? {
      type: raw.type,
      id: raw.id,
      title: raw.title,
      generatedAt: raw.generated_at ?? undefined
    }
    : {
      type: raw.type,
      id: raw.id,
      title: raw.title,
      detectedMime: raw.mimetype ?? undefined
    };

export const toConfirmedReference = (content: Record<string, unknown>): IMessageReference | null => {
  const { type, id, title } = content;
  if (typeof id !== 'string' || typeof title !== 'string') {
    return null;
  }
  if (type === 'knowledge_document') {
    return toMessageReference({
      type,
      id,
      title,
      mimetype: typeof content.mimetype === 'string' ? content.mimetype : null
    });
  }
  if (type === 'addressable_information') {
    return toMessageReference({
      type,
      id,
      title,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      generated_at: typeof content.generated_at === 'string' ? content.generated_at : null
    });
  }
  return null;
};

export const toKnowledgeDocumentReference = (
  document: ISelectedKnowledgeDocument
): IKnowledgeDocumentReference => ({
  type: 'knowledge_document',
  id: document.id,
  title: document.title,
  originalFilename: document.originalFilename,
  detectedMime: document.detectedMime
});

export const toInformationReference = (
  information: ISelectedInformation
): IInformationReference => ({
  type: 'addressable_information',
  id: information.id,
  title: information.title,
  generatedAt: information.generatedAt
});

export const toOptionalReferences = (
  document?: ISelectedKnowledgeDocument,
  information?: ISelectedInformation
): IMessageReference[] | undefined => {
  const references: IMessageReference[] = [
    ...(document ? [toKnowledgeDocumentReference(document)] : []),
    ...(information ? [toInformationReference(information)] : [])
  ];
  return references.length ? references : undefined;
};

export const isKnowledgeDocumentReference = (
  reference: IMessageReference
): reference is IKnowledgeDocumentReference => reference.type === 'knowledge_document';

export const isInformationReference = (
  reference: IMessageReference
): reference is IInformationReference => reference.type === 'addressable_information';
